import React, { useRef, useEffect, useState, useMemo } from 'react';
import { KeypointCollectionLabel, KeypointFrame } from 'models/keypoints';
import { KeypointOverlayDiv, KeypointDot } from './KeypointOverlay.styled';

interface KeypointOverlayProps {
  frame: KeypointFrame;
  keypoints: KeypointCollectionLabel[];
  displayDims?: {
    left: number;
    top: number;
    width: number;
    height: number;
    imgW: number;
    imgH: number;
  };
  // Optional fallback mapping from class UUID to pair id when frame labels don't include pairing
  pairIdByClassUuid?: Record<string, string>;
}

const KeypointOverlay: React.FC<KeypointOverlayProps> = ({
  frame,
  keypoints,
  displayDims,
  pairIdByClassUuid,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [imgDims, setImgDims] = useState({
    width: 1,
    height: 1,
    left: 0,
    top: 0,
    imgW: 1,
    imgH: 1,
  });
  const [hovered, setHovered] = useState<null | {
    x: number;
    y: number;
    left: number;
    top: number;
  }>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const lastFrameUrl = useRef<string | undefined>(undefined);
  const frameUrl = frame?.fileUrl;

  // Keep overlay in sync with container; if explicit displayDims provided, trust it
  useEffect(() => {
    if (displayDims) {
      const valid =
        displayDims.width > 0 &&
        displayDims.height > 0 &&
        displayDims.imgW > 0 &&
        displayDims.imgH > 0;
      if (valid) {
        setImgDims(displayDims);
        setImgLoaded(true);
        return;
      }
    }

    if (!overlayRef.current) return;

    const ro = new ResizeObserver(() => {
      if (frameUrl && lastFrameUrl.current === frameUrl) {
        const img = new window.Image();
        img.onload = function () {
          if (!overlayRef.current) return;
          const containerW = overlayRef.current.offsetWidth;
          const containerH = overlayRef.current.offsetHeight;
          const imgW = img.naturalWidth;
          const imgH = img.naturalHeight;
          const scale = Math.min(containerW / imgW, containerH / imgH);
          const displayW = imgW * scale;
          const displayH = imgH * scale;
          const left = (containerW - displayW) / 2;
          const top = (containerH - displayH) / 2;
          setImgDims({ width: displayW, height: displayH, left, top, imgW, imgH });
        };
        img.src = frameUrl;
      }
    });
    ro.observe(overlayRef.current);

    return () => ro.disconnect();
  }, [frameUrl, displayDims]);

  const toDisplayPosition = (x: number, y: number) => {
    // Accept both pixel and normalized coordinates
    const isNormalized = x <= 1 && y <= 1;
    const normalizedX = isNormalized ? x : x / imgDims.imgW;
    const normalizedY = isNormalized ? y : y / imgDims.imgH;

    // Match Atlas behavior (top-left origin) within the image box only
    const left = ((normalizedX * imgDims.width) / imgDims.width) * 100;
    const top = ((normalizedY * imgDims.height) / imgDims.height) * 100; // Remove Y-axis inversion, assume top-down coordinates
    return { left, top };
  };

  // Build segments between paired keypoints using label.keypointClass.keypointPair when available
  // Fallback to pairIdByClassUuid if provided
  const pairedSegments = useMemo(() => {
    if (!imgLoaded) return [] as Array<{ x1: number; y1: number; x2: number; y2: number }>;

    // Collect points by class uuid
    const pointByClassUuid: Record<string, { x: number; y: number }> = {};
    const labelMeta: Array<{
      classUuid?: string;
      pairUuid?: string;
      x: number;
      y: number;
      name?: string;
    }> = [];

    keypoints.forEach(kp => {
      (kp.keypointLabels || []).forEach((label: any) => {
        const klass = label?.keypointClass;
        const classUuid = typeof klass === 'string' ? klass : klass?.uuid;
        const pairUuid = typeof klass === 'object' ? (klass?.keypointPair ?? undefined) : undefined;
        const x = label.x ?? 0;
        const y = label.y ?? 0;
        const { left, top } = toDisplayPosition(x, y);
        const px = left;
        const py = top;
        if (classUuid) {
          pointByClassUuid[classUuid] = { x: px, y: py };
        }
        labelMeta.push({
          classUuid,
          pairUuid,
          x: px,
          y: py,
          name: typeof klass === 'object' ? klass?.value : undefined,
        });
      });
    });

    const segments: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    const seen = new Set<string>();

    // Prefer explicit pairing from frame labels
    labelMeta.forEach(l => {
      if (!l.classUuid || !l.pairUuid) return;
      const a = l.classUuid;
      const b = l.pairUuid;
      const key = [a, b].sort().join('::');
      if (seen.has(key)) return;
      const p1 = pointByClassUuid[a];
      const p2 = pointByClassUuid[b];
      if (p1 && p2) {
        segments.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y });
        seen.add(key);
      }
    });

    // Fallback to view-based pairing if no explicit pairs found
    if (segments.length === 0 && pairIdByClassUuid) {
      const pointsByPairId: Record<string, Array<{ x: number; y: number }>> = {};
      Object.entries(pointByClassUuid).forEach(([classUuid, pt]) => {
        const pid = pairIdByClassUuid[classUuid];
        if (!pid) return;
        if (!pointsByPairId[pid]) pointsByPairId[pid] = [];
        pointsByPairId[pid].push(pt);
      });
      Object.values(pointsByPairId).forEach(points => {
        if (points.length === 2) {
          segments.push({ x1: points[0].x, y1: points[0].y, x2: points[1].x, y2: points[1].y });
        }
      });
    }

    return segments;
  }, [imgLoaded, keypoints, imgDims, pairIdByClassUuid]);

  return (
    <>
      <KeypointOverlayDiv
        ref={overlayRef}
        style={{
          left: imgDims.left,
          top: imgDims.top,
          width: imgDims.width,
          height: imgDims.height,
        }}
      >
        {imgLoaded && (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
          >
            {pairedSegments.map((seg, idx) => (
              <line
                key={idx}
                x1={seg.x1}
                y1={seg.y1}
                x2={seg.x2}
                y2={seg.y2}
                stroke="#00e0ff"
                strokeWidth={0.6}
                opacity={0.95}
              />
            ))}
          </svg>
        )}
        {imgLoaded &&
          keypoints
            .filter(kp => kp.keypointLabels && kp.keypointLabels.length > 0)
            .flatMap(kp =>
              kp.keypointLabels.map((label: any, index: number) => {
                const x = label.x || 0;
                const y = label.y || 0;
                const { left, top } = toDisplayPosition(x, y);
                return (
                  <React.Fragment key={`${kp.icid}-${index}`}>
                    <KeypointDot
                      style={{ left: `${left}%`, top: `${top}%` }}
                      onMouseEnter={() => setHovered({ x, y, left, top })}
                      onMouseLeave={() => setHovered(null)}
                    />
                    {hovered && hovered.x === x && hovered.y === y && (
                      <div
                        style={{
                          position: 'absolute',
                          left: `calc(${left}% + 10px)`,
                          top: `calc(${top}% - 10px)`,
                          background: 'rgba(0,0,0,0.85)',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          pointerEvents: 'none',
                          whiteSpace: 'nowrap',
                          zIndex: 100,
                        }}
                      >
                        {(() => {
                          const klass = label?.keypointClass;
                          const name = typeof klass === 'object' ? klass?.value : undefined;
                          const prefix = name || kp.keypointCollectionClassValue;
                          return prefix ? `${prefix} (${x}, ${y})` : `(${x}, ${y})`;
                        })()}
                      </div>
                    )}
                  </React.Fragment>
                );
              })
            )}
      </KeypointOverlayDiv>
    </>
  );
};

export default KeypointOverlay;
